// src/components/admin/BespokeAIHub.jsx
// Floating Bespoke AI hub — chip image icon, hub-and-spoke animation, fully responsive
import React, { useState, useEffect, useRef } from 'react';
import '../../styles/bespoke-ai-hub.css';
import chipImage from '../../assets/bespoke-ai-chip.jpg';

// ── Spoke definitions ────────────────────────────────────────────────────────
// Hub lives in the bottom-RIGHT corner.
//
// CSS angle convention used in spokePos():
//   0°  = right,  90° = DOWN,  180° = left,  270° = UP
//
// User's angle convention (standard math / compass from horizontal):
//   90° from horizontal = straight UP   → CSS 270°
//   80° from horizontal = 10° east of north → CSS 280°
//   Lower angles (193°–247°) fan left for the remaining three spokes.
//
// Core AI gets a 15% longer spoke radius (radiusMultiplier: 1.15).
const SPOKES = [
  { id: 'communications-ai', label: 'Communications AI', tabId: 'communications-ai-agent', moduleId: 'communications', angle: 193,              radiusMultiplier: 1    },
  { id: 'loyalty-ai',        label: 'Loyalty AI',        tabId: 'loyalty-ai-agent',        moduleId: 'promotions',     angle: 211,              radiusMultiplier: 1    },
  { id: 'bookings-ai',       label: 'Bookings AI',       tabId: 'bookings-ai-agent',       moduleId: 'bookings',       angle: 229,              radiusMultiplier: 1    },
  { id: 'reports-ai',        label: 'Reports AI',        tabId: 'reports-ai-agent',        moduleId: 'reports',        angle: 252, /* NW / 72° from horizontal */ radiusMultiplier: 1    },
  { id: 'core-ai',           label: 'Core AI',           tabId: 'core-ai-agent',           moduleId: 'core',           angle: 270, /* N  / 90° */ radiusMultiplier: 1.15 },
];

// ── Responsive orbit radius ──────────────────────────────────────────────────
function getOrbitRadius() {
  const w = window.innerWidth;
  if (w < 430)  return 100;   // iPhone 17, S23 Ultra
  if (w < 576)  return 108;
  if (w < 768)  return 116;
  if (w < 992)  return 126;   // Oppo Pad 3
  return 148;                  // laptops / desktops
}

function spokePos(angleDeg, radius) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: Math.cos(rad) * radius, y: Math.sin(rad) * radius };
}

function useOrbitRadius() {
  const [radius, setRadius] = useState(getOrbitRadius);
  useEffect(() => {
    const onResize = () => setRadius(getOrbitRadius());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return radius;
}

// ── Component ────────────────────────────────────────────────────────────────
export default function BespokeAIHub({ isTabAvailable, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref    = useRef(null);
  const radius = useOrbitRadius();

  // Close on outside click / tap
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const handleSpokeClick = (spoke) => {
    if (!isTabAvailable(spoke.tabId)) return;
    setOpen(false);
    onNavigate(spoke.moduleId, spoke.tabId);
  };

  return (
    <div className="bai-hub-root" ref={ref}>

      {/* Dashed connector lines — viewBox centred on hub */}
      <svg
        className={`bai-connector-svg${open ? ' bai-connector-svg--visible' : ''}`}
        viewBox="-170 -170 340 340"
        aria-hidden="true"
      >
        {SPOKES.map((spoke) => {
          const pos = spokePos(spoke.angle, radius * spoke.radiusMultiplier);
          return (
            <line key={spoke.id}
              x1="0" y1="0" x2={pos.x} y2={pos.y}
              stroke="#555" strokeWidth="1.2" strokeDasharray="4 3" opacity="0.5"
            />
          );
        })}
      </svg>

      {/* Spoke pills */}
      {SPOKES.map((spoke, i) => {
        const pos       = spokePos(spoke.angle, radius * spoke.radiusMultiplier);
        const available = isTabAvailable(spoke.tabId);
        return (
          <div
            key={spoke.id}
            className={`bai-spoke${open ? ' bai-spoke--visible' : ''}${available ? '' : ' bai-spoke--dim'}`}
            style={{
              '--tx': `${pos.x}px`,
              '--ty': `${pos.y}px`,
              transitionDelay: open
                ? `${i * 50}ms`
                : `${(SPOKES.length - 1 - i) * 35}ms`,
            }}
            onClick={() => handleSpokeClick(spoke)}
            title={available ? spoke.label : `${spoke.label} — not available`}
          >
            <span className="bai-spoke-label">{spoke.label}</span>
          </div>
        );
      })}

      {/* Hub button — uses the actual uploaded chip image */}
      <button
        className={`bai-hub-btn${open ? ' bai-hub-btn--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open Bespoke AI Hub"
      >
        <img
          src={chipImage}
          alt="Bespoke AI"
          className="bai-hub-img"
          draggable="false"
        />
      </button>
    </div>
  );
}
